import API_ENDPOINTS from '@/common/constants/api'
import { applyListboxWireTypesFromListboxFields } from '@/common/utils/comboBoxSwingFields'
import { normalizePlatformFlowContextCurrentData } from '@/common/utils/normalizePlatformFlowContextFields'
import { ensureUniDataValueForJackson } from '@/common/utils/typedValueSerializer'
import { stripTreeTableServerOnlyAuxFromPlatformRequest } from '@/common/utils/stripTreeTableServerOnlyAux'
import type { AxiosRequestConfig } from 'axios'
import api from './api'

const MAX_PLAIN_PAYLOAD_DEPTH = 200

/** Vue/reactivity internal keys */
const VUE_INTERNAL_KEYS = new Set([
  '__v_isRef',
  '__v_isReactive',
  '__v_isShallow',
  '__v_skip',
  '_rawValue',
  '_value',
  'dep',
  'deps',
  'effect',
  'target',
  'currentTarget'
])

function getDefaultValueForFieldType(type?: unknown): any {
  const normalizedType = typeof type === 'string' ? type : String(type ?? '')
  switch (normalizedType) {
    case 'UniDataTable':
    case 'UniDataTableFields':
      return { attributes: {}, columns: [], rows: [], empty: true }
    case 'Boolean':
      return false
    case 'Long':
    case 'Double':
    case 'BigDecimal':
    case 'Integer':
      return 0
    default:
      return ''
  }
}

function normalizeTypedFieldsMap(fields: any) {
  if (!fields || typeof fields !== 'object') return

  for (const key of Object.keys(fields)) {
    const field = fields[key]
    if (!field || typeof field !== 'object' || !('type' in field)) continue
    const t = (field as any).type
    const v = (field as any).value
    if (!('value' in field) || v === undefined || v === null) {
      fields[key] = {
        ...(field as any),
        value: getDefaultValueForFieldType(t),
      }
    }
    const val = (fields[key] as any).value
    if (String(t) === 'UniData' && val != null && typeof val === 'object') {
      fields[key] = {
        ...(fields[key] as any),
        value: ensureUniDataValueForJackson(val),
      }
    }
    const rowType = ['UniDataTable', 'UniDataTableFields'].includes(String(t))
    const valAfter = (fields[key] as any).value
    if (rowType && valAfter && typeof valAfter === 'object' && Array.isArray(valAfter.rows)) {
      for (const row of valAfter.rows) {
        if (row?.fields && typeof row.fields === 'object') {
          normalizeTypedFieldsMap(row.fields)
        }
      }
    }
  }
}

function normalizeExchangeDataPayload(exchangeData: any) {
  if (!exchangeData || typeof exchangeData !== 'object') return

  if (!exchangeData.fields || typeof exchangeData.fields !== 'object') {
    exchangeData.fields = {
      fields: {},
      fieldAttributes: {},
      errors: {},
      attributes: {},
    }
  }

  if (!exchangeData.fields.fields || typeof exchangeData.fields.fields !== 'object') {
    exchangeData.fields.fields = {}
  }
  if (!exchangeData.fields.fieldAttributes || typeof exchangeData.fields.fieldAttributes !== 'object') {
    exchangeData.fields.fieldAttributes = {}
  }
  if (!exchangeData.fields.errors || typeof exchangeData.fields.errors !== 'object') {
    exchangeData.fields.errors = {}
  }
  if (!exchangeData.fields.attributes || typeof exchangeData.fields.attributes !== 'object') {
    exchangeData.fields.attributes = {}
  }

  normalizeTypedFieldsMap(exchangeData.fields.fields)
  applyListboxWireTypesFromListboxFields(exchangeData.fields.fields)
}

/**
 * Recursively converts value to a plain serializable object (no Vue refs/reactive, no circular refs).
 */
function toPlainPayload(value: any, seen = new WeakSet(), depth = 0): any {
  if (depth > MAX_PLAIN_PAYLOAD_DEPTH) return undefined
  if (value === null) return null
  if (typeof value !== 'object') return value
  if (typeof value === 'function') return undefined
  if ((value as any).__v_isRef === true) {
    if (seen.has(value)) return undefined
    seen.add(value)
    return toPlainPayload((value as any).value, seen, depth + 1)
  }
  if (value.constructor?.name === 'ReactiveEffect') return undefined
  if (Array.isArray(value)) return value.map((item) => toPlainPayload(item, seen, depth + 1))

  if (
    typeof (value as any).type === 'string' &&
    Object.prototype.hasOwnProperty.call(value, 'value') &&
    !Array.isArray(value)
  ) {
    const fieldType = String((value as any).type)
    const innerSeen = new WeakSet()
    let valuePlain = toPlainPayload((value as any).value, innerSeen, depth + 1)
    if (valuePlain === undefined || valuePlain === null) {
      valuePlain = getDefaultValueForFieldType(fieldType)
    }
    const result: Record<string, any> = { type: fieldType, value: valuePlain }
    for (const key of Object.keys(value)) {
      if (key === 'type' || key === 'value' || VUE_INTERNAL_KEYS.has(key)) continue
      const plain = toPlainPayload((value as any)[key], seen, depth + 1)
      if (plain !== undefined) result[key] = plain
    }
    return result
  }

  if (seen.has(value)) return undefined
  seen.add(value)
  const result: Record<string, any> = {}
  for (const key of Object.keys(value)) {
    if (VUE_INTERNAL_KEYS.has(key)) continue
    const plain = toPlainPayload(value[key], seen, depth + 1)
    if (plain !== undefined) result[key] = plain
  }
  return result
}

function sanitizeRequestData(data: any): any {
  if (!data) return data

  if (data.clientData) {
    normalizeExchangeDataPayload(data.clientData)
  }

  // Ensure clientData.clientData exists (UniClientData)
  if (data.clientData) {
    if (!data.clientData.clientData || typeof data.clientData.clientData !== 'object') {
      data.clientData.clientData = {
        elementConditions: null,
        reloadActions: null,
        params: {},
        doActionId: 0,
        openPage: 0
      }
    } else {
      if (!data.clientData.clientData.params || typeof data.clientData.clientData.params !== 'object') {
        data.clientData.clientData.params = {}
      }
      if (data.clientData.clientData.doActionId === undefined || data.clientData.clientData.doActionId === null) {
        data.clientData.clientData.doActionId = 0
      }
      if (data.clientData.clientData.openPage === undefined || data.clientData.clientData.openPage === null) {
        data.clientData.clientData.openPage = 0
      }
    }
  }

  if (data.clientData && data.clientData.formError === undefined) {
    data.clientData.formError = null
  }

  if (data.clientData) {
    data['currentData'] = data['clientData']
    delete data['clientData']
  }

  if (data.currentData) {
    normalizeExchangeDataPayload(data.currentData)
  }

  return toPlainPayload(data)
}

export const apiService = {
  get: <T>(url: string, config?: AxiosRequestConfig) =>
    api.get<T>(url, config).then(response => response.data),

  post: <T>(url: string, data?: any, config?: AxiosRequestConfig) => {
    const sanitizedData = sanitizeRequestData(data)

    return api.post<T>(url, sanitizedData, config).then(response => response.data)
  },

  put: <T>(url: string, data?: any, config?: AxiosRequestConfig) => {
    const sanitizedData = sanitizeRequestData(data)
    return api.put<T>(url, sanitizedData, config).then(response => response.data)
  },

  patch: <T>(url: string, data?: any, config?: AxiosRequestConfig) => {
    const sanitizedData = sanitizeRequestData(data)
    return api.patch<T>(url, sanitizedData, config).then(response => response.data)
  },

  delete: <T>(url: string, config?: AxiosRequestConfig) =>
    api.delete<T>(url, config).then(response => response.data),

  // описание
  async platformAction(data: any) {
    const sanitizedData = sanitizeRequestData(data)
    // console.log('sanitize', sanitizedData)
    stripTreeTableServerOnlyAuxFromPlatformRequest(sanitizedData)
    const config = API_ENDPOINTS.platform.action


    return api({
      ...config,
      data: sanitizedData,
    })
  },

  async platformReloadElement(data: any) {
    const sanitizedData = sanitizeRequestData(data)
    stripTreeTableServerOnlyAuxFromPlatformRequest(sanitizedData)
    const config = API_ENDPOINTS.platform.reloadElement
    return api({
      ...config,
      data: sanitizedData
    })
  },

  async platformReloadPanel(data: any) {
    const sanitizedData = sanitizeRequestData(data)
    stripTreeTableServerOnlyAuxFromPlatformRequest(sanitizedData)
    const config = API_ENDPOINTS.platform.reloadPanel
    return api({
      ...config,
      data: sanitizedData
    })
  },

  async platformReloadForm(data: any) {
    const sanitizedData = sanitizeRequestData(data)
    stripTreeTableServerOnlyAuxFromPlatformRequest(sanitizedData)
    const config = API_ENDPOINTS.platform.reloadForm
    return api({
      ...config,
      data: sanitizedData
    })
  },

  async platformExecuteProcedure(data: any) {
    const sanitizedData = sanitizeRequestData(data)
    normalizePlatformFlowContextCurrentData(sanitizedData.currentData)
    const config = API_ENDPOINTS.platform.executeProcedure
    return api({
      ...config,
      data: sanitizedData
    })
  },

  async platformExecuteScript(data: any) {
    const sanitizedData = sanitizeRequestData(data)
    stripTreeTableServerOnlyAuxFromPlatformRequest(sanitizedData)
    const config = API_ENDPOINTS.platform.executeScript


    return api({
      ...config,
      data: sanitizedData
    })
  },

  async platformRemove(data: any) {
    const sanitizedData = sanitizeRequestData(data)
    const config = API_ENDPOINTS.platform.remove
    return api({
      ...config,
      data: sanitizedData
    })
  },

  async platformConfig() {
    const config = API_ENDPOINTS.platform.config
    return api(config).then(response => response.data)
  },

  async platformStartupActionsPending(): Promise<{
    pending?: boolean
    firstDate?: string
    actions?: Array<{ id: number; windowType?: string; addedDate?: string }>
  }> {
    const config = API_ENDPOINTS.platform.startupActionsPending
    return api(config).then(response => response.data)
  },

  async getAppVersion() {
    const config = API_ENDPOINTS.app.version
    return api(config).then(response => response.data as { version?: string })
  },

  /**
   * Методы для пользователя
   */
  async getUserSystemsList() {
    const config = API_ENDPOINTS.user.getSystemsList
    return api({
      ...config
    }).then(response => response.data)
  },

  async userSignIn(data: {
    username: string
    password: string
    systemAlias?: string | null
    clientVersionDate?: string | null
  }) {
    const config = API_ENDPOINTS.user.signIn
    return api({
      ...config,
      data
    }).then(response => response.data)
  },

  async userSignInByCard(data: {
    smartCardNo: string
    systemAlias?: string | null
    clientVersionDate?: string | null
  }) {
    const config = API_ENDPOINTS.user.signInByCard
    return api({
      ...config,
      data
    }).then(response => response.data)
  },

  async userSignOut() {
    const config = API_ENDPOINTS.user.signout
    return api(config).then(response => response.data)
  },

  async getUser() {
    const config = API_ENDPOINTS.user.get

    return api.get(config.url).then(response => response.data)
  },

  async getPlatformConnectionStatus(): Promise<string> {
    const config = API_ENDPOINTS.user.platformConnectionStatus
    return api({
      ...config,
    }).then((response) => String(response.data ?? ''))
  },

  async refreshToken(data: any) {
    const config = API_ENDPOINTS.user.refreshToken
    return api({
      ...config,
      data
    }).then(response => response.data)
  },

  async changePassword(data: any) {
    const config = API_ENDPOINTS.user.changePassword
    return api({
      ...config,
      data
    }).then(response => response.data)
  },

  /** POST /user/change-user — смена пользователя без пароля; ответ = PlatformConfig. */
  async userChangeUser(data: any) {
    const config = API_ENDPOINTS.user.changeUser
    return api({
      ...config,
      data,
    }).then((response) => response.data)
  },
}

export default apiService
